
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Heart } from 'lucide-react';
import { useAuth } from '@/contexts/auth';
import { supabase } from '@/integrations/supabase/client';
import { ContentType } from '@/services/comments/types';

interface LikeButtonProps {
  cardId: string;
  contentType?: ContentType;
  initialLikes?: number;
  showCount?: boolean;
  size?: 'sm' | 'md' | 'lg';
  onLikeClick?: () => void;
}

const LikeButton: React.FC<LikeButtonProps> = ({ 
  cardId, 
  contentType = 'card',
  initialLikes = 0,
  showCount = true,
  size = 'md',
  onLikeClick
}) => {
  const { user, isAuthenticated } = useAuth();
  const [likesCount, setLikesCount] = useState(initialLikes);
  const [isLiked, setIsLiked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch like data on mount
  useEffect(() => {
    const fetchLikeData = async () => {
      try {
        setIsLoading(true);
        
        // Check if content is already liked by the current user
        if (isAuthenticated && user?.id) {
          if (contentType === 'poll') {
            const { data, error } = await supabase
              .from('poll_likes')
              .select('id')
              .eq('poll_id', cardId)
              .eq('user_id', user.id)
              .maybeSingle();
            
            if (error && error.code !== 'PGRST116') {
              console.error('Error checking poll like status:', error);
            }
            
            setIsLiked(!!data);
          } else {
            const { data, error } = await supabase
              .from('card_likes')
              .select('id')
              .eq('card_id', cardId)
              .eq('user_id', user.id)
              .maybeSingle();
            
            if (error && error.code !== 'PGRST116') {
              console.error('Error checking card like status:', error);
            }
            
            setIsLiked(!!data);
          }
        }
        
        // Get total likes count
        if (contentType === 'poll') {
          const { count, error: likesError } = await supabase
            .from('poll_likes')
            .select('id', { count: 'exact', head: true })
            .eq('poll_id', cardId);
          
          if (!likesError) {
            setLikesCount(count || initialLikes);
          }
        } else {
          const { count, error: likesError } = await supabase
            .from('card_likes')
            .select('id', { count: 'exact', head: true })
            .eq('card_id', cardId);
          
          if (!likesError) {
            setLikesCount(count || initialLikes);
          }
        }
      } catch (error) {
        console.error('Error fetching like data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (cardId) {
      fetchLikeData();
    }
  }, [cardId, user?.id, isAuthenticated, contentType, initialLikes]);

  const handleLike = async () => {
    try {
      if (!isAuthenticated) {
        console.log('Please log in to like this content');
        return;
      }

      // Custom handler if provided
      if (onLikeClick) {
        onLikeClick();
        return;
      }

      // Optimistically update UI
      const currentLikedState = isLiked;
      setIsLiked(!currentLikedState);
      setLikesCount(prev => currentLikedState ? prev - 1 : prev + 1);

      if (currentLikedState) {
        // Unlike: Delete the like
        if (contentType === 'poll') {
          const { error } = await supabase
            .from('poll_likes')
            .delete()
            .eq('poll_id', cardId)
            .eq('user_id', user!.id);
            
          if (error) {
            console.error('Error removing poll like:', error);
            // Revert optimistic updates
            setIsLiked(true);
            setLikesCount(prev => prev + 1);
            console.log('Failed to unlike');
          }
        } else {
          const { error } = await supabase
            .from('card_likes')
            .delete()
            .eq('card_id', cardId)
            .eq('user_id', user!.id);
            
          if (error) {
            console.error('Error removing card like:', error);
            // Revert optimistic updates
            setIsLiked(true);
            setLikesCount(prev => prev + 1);
            console.log('Failed to unlike');
          }
        }
      } else {
        // Like: Add a new like
        if (contentType === 'poll') {
          const { error } = await supabase
            .from('poll_likes')
            .insert({
              poll_id: cardId,
              user_id: user!.id
            });
              
          if (error) {
            console.error('Error adding poll like:', error);
            // Revert optimistic updates
            setIsLiked(false);
            setLikesCount(prev => prev - 1);
          }
        } else {
          const { error } = await supabase
            .from('card_likes')
            .insert({
              card_id: cardId,
              user_id: user!.id
            });
              
          if (error) {
            console.error('Error adding card like:', error);
            // Revert optimistic updates
            setIsLiked(false);
            setLikesCount(prev => prev - 1);
          }
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert optimistic updates on any error
      const currentLikedState = isLiked;
      setIsLiked(!currentLikedState);
      setLikesCount(prev => currentLikedState ? prev + 1 : prev - 1);
      console.log('Something went wrong');
    }
  };

  // Size variants
  const sizeClasses = {
    sm: 'h-8 px-3 text-xs',
    md: 'h-9 px-4',
    lg: 'h-10 px-6'
  };

  return (
    <Button
      variant={isLiked ? "default" : "outline"}
      className={`${sizeClasses[size]} ${isLiked ? 'bg-red-500 hover:bg-red-600 border-red-500' : ''}`}
      onClick={handleLike}
      disabled={isLoading}
      size={size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'default'}
    >
      <Heart className={`${isLiked ? 'fill-white' : ''} mr-1 h-4 w-4`} />
      {showCount && (
        <span>{likesCount}</span>
      )}
    </Button>
  );
};

export default LikeButton;
